const extraParams: Record<string, string> = {}

// VITE_DEBUG_GA='on' logs every GA event to the console and enables GA in dev mode (separate non-prod GA property);
// any other value ('off', empty, unset) disables both - compared explicitly, a non-empty 'off' string is truthy
function isDebugGa() {
  return import.meta.env.VITE_DEBUG_GA === 'on'
}

// whether gtag.js may be loaded (after cookie consent) - always in builds, in dev mode only with VITE_DEBUG_GA='on'
function canLoadGtag() {
  if (import.meta.env.SSR) {
    return false
  }
  return import.meta.env.MODE !== 'development' || isDebugGa()
}

const CLIENT_ID_KEY = 'gs_client_id'
const CLIENT_ID_TIMEOUT_MS = 3000
// btoa('Mozilla/5.0 (') - the old generator only encoded the user agent prefix, so these ids are shared by many users
// (the store replaces them the same way)
const LEGACY_CLIENT_ID_PREFIX = 'TW96aWxsYS81LjAgK'

let clientId = ''

// random 22-char id - the previous btoa(userAgent + …).slice(0, 22) only encoded the UA prefix,
// so every browser with the same UA start got the same id (and btoa throws on non-Latin1 chars)
function generateDeviceId() {
  const uuid = globalThis.crypto?.randomUUID?.()
  if (uuid) {
    return uuid.replace(/-/g, '').slice(0, 22)
  }
  return Array.from({ length: 22 }, () => Math.floor(Math.random() * 36).toString(36)).join('')
}

function isLegacyClientId(id: string) {
  if (id.startsWith(LEGACY_CLIENT_ID_PREFIX)) {
    return true
  }
  try {
    return id === btoa(navigator.userAgent).slice(0, 22)
  } catch {
    return false // non-Latin1 user agent - the old generator threw there, so no legacy id exists
  }
}

// gs_client_id - per-device id sent as a GA event param; '' until established
// (stored also without GA consent - it was the A/B tests session id; whether to keep that is an open question)
function getClientId(): string {
  if (!clientId && !import.meta.env.SSR) {
    try {
      const stored = localStorage.getItem(CLIENT_ID_KEY) || ''
      if (stored && !isLegacyClientId(stored)) {
        clientId = stored
        extraParams.gs_client_id = stored
      }
    } catch {
      // storage unavailable - wait for an in-memory id
    }
  }
  return clientId
}

// the only writer of gs_client_id - first id wins and is never replaced (stable per-device id)
function setClientId(id: string) {
  if (getClientId()) {
    return
  }
  clientId = id
  extraParams.gs_client_id = id
  try {
    localStorage.setItem(CLIENT_ID_KEY, id)
  } catch {
    // storage unavailable - id kept in memory for this page load
  }
}

// cookie decision - stored (page load) or just made; with GA the id comes from onReadyVueGtag (GA client_id),
// without GA (refused, or dev mode without VITE_DEBUG_GA='on') our random id is set right away
function onConsentDecision(gaAllowed: boolean) {
  if (!gaAllowed || !canLoadGtag()) {
    setClientId(generateDeviceId())
  }
}

function onReadyVueGtag(gtagId: string) {
  if (getClientId() || typeof window?.gtag !== 'function') {
    setClientId(generateDeviceId()) // no-op when an id already exists
    return
  }
  let resolved = false
  // prefer the GA client_id; the gtag 'get' callback may never fire (e.g. blocked request) - fall back after a timeout
  const resolveClientId = (gaClientId: string) => {
    if (resolved) {
      return
    }
    resolved = true
    setClientId(gaClientId?.length ? gaClientId : generateDeviceId()) // no-op if an id was set meanwhile
  }
  window.gtag('get', gtagId, 'client_id', resolveClientId)
  setTimeout(resolveClientId, CLIENT_ID_TIMEOUT_MS, '')
}

const contextGaRef = ref<GaUtils.GaContext>({})
// sent params = the event's own context + site-wide params (gs_client_id, gs_user_id)
type GaSentParams = GaUtils.GaContext & Record<string, unknown>

// params attached to every GA event sent by GOAT (gaEvent and the router page_view) - unchanged from the former gaEvent:
// an explicit params.gs_user_id passes through, the logged-in GOAT user overrides it
function gaCommonParams(params: GaUtils.GaContext): GaSentParams {
  const auth = useAuthStore()
  const { getAuthFullData } = auth
  return {
    ...params,
    ...extraParams,
    ...(getAuthFullData?.id ? { gs_user_id: `goat_${getAuthFullData.id}` } : {})
  }
}

// VITE_DEBUG_GA console log - shows exactly what is sent
function logDebugGa(eventName: string, params: GaUtils.GaContext) {
  if (isDebugGa()) {
    // eslint-disable-next-line no-console
    console?.log({ eventName, params }, 'VITE_DEBUG_GA')
  }
}

// run the callback once GA can send (cookie consent given and gtag.js loaded) - events sent earlier are dropped (no queue);
// never runs without consent. Detached effect scope: called from a component setup, the watcher must survive leaving the page.
function whenGaReady(callback: () => void) {
  if (import.meta.env.SSR) {
    return
  }
  if (appUtilsPlugin.siteGtag()) {
    callback()
    return
  }
  const scope = effectScope(true)
  scope.run(() => {
    watch(() => Boolean(appUtilsPlugin.siteGtag()), (isReady) => {
      if (isReady) {
        scope.stop()
        callback()
      }
    })
  })
}

function gaEvent(eventName: string, params: GaUtils.GaContext) {
  if (!appUtilsPlugin.siteGtag() && !isDebugGa()) {
    return // no consent / GA not loaded - nothing to send or log (and no store access, e.g. during SSG)
  }
  const eventParams = gaCommonParams(params)
  appUtilsPlugin.siteGtag()?.event(eventName, eventParams)
  logDebugGa(eventName, eventParams)
}
enum gaSections {
  registerForm = 'registerForm',
  loginForm = 'loginForm',
  storePage = 'storePage',
  app = 'app'
}
enum gaLoginMethods {
  goat = 'goat'
}
export type LoginMethods = `${gaLoginMethods}`
export interface LoginContext extends GaUtils.GaContext {
  method?: string
  connectMode?: boolean
}
function gaLogin(params: LoginContext): void {
  gaEvent('login', params)
}
export interface PageViewContext extends GaUtils.GaContext {
  page_title?: string
  page_location?: string
  page_path?: string
}
// params for the router page_view (vue-gtag pageview() in routerInitPlugin.ts - the only page_view sender, it also
// handles UTM -> campaign); call only when GA is ready - the debug log shows what is sent
function gaPageViewParams(params: PageViewContext): GaSentParams {
  const pageViewParams = gaCommonParams(params)
  logDebugGa('page_view', pageViewParams)
  return pageViewParams
}
function gaConnectBtnClick(params: GaUtils.GaContext) {
  gaEvent('connect_btn_click', params)
}
enum gaSignUpMethods {
  gmail = 'gmail',
  facebook = 'facebook',
  goatForm = 'goatForm'
}
export type SignUpMethods = `${gaSignUpMethods}` | string
export interface SignUpContext extends GaUtils.GaContext {
  method?: SignUpMethods
}
function gaSignUp(params: SignUpContext) {
  gaEvent('sign_up', params)
}
function gaVerifyBtnClick(params: GaUtils.GaContext) {
  gaEvent('verify_btn_click', params)
}
function gaRemoveAccount() {
  gaEvent('remove_account', {})
}
function gaChangeCommunication(params: SignUpContext) {
  gaEvent('change_communication', params)
}
function gaLogoutCharacter(params: SignUpContext) {
  gaEvent('logout_character', params)
}
function gaAllDealsBtnClick(params: GaUtils.GaContext) {
  gaEvent('all_deals_btn_click', params)
}
interface ItemsContext {
  item_id?: string
  item_name?: string
  index?: number // position of the item in the list
  quantity?: number
  price?: number
}
export interface SelectPromotionContext extends GaUtils.GaContext {
  promotion_name?: string
  promotion_id?: string
  items?: ItemsContext[]
}
function gaSelectPromotion(params: SelectPromotionContext) {
  gaEvent('select_promotion', params)
}
const gaUtils = {
  gaLogin,
  gaLoginMethods,
  gaSections,
  gaConnectBtnClick,
  contextGaRef,
  gaSignUp,
  gaSignUpMethods,
  onReadyVueGtag,
  isDebugGa,
  canLoadGtag,
  getClientId,
  onConsentDecision,
  whenGaReady,
  gaVerifyBtnClick,
  gaRemoveAccount,
  gaChangeCommunication,
  gaLogoutCharacter,
  gaSelectPromotion,
  gaAllDealsBtnClick,
  gaPageViewParams
}
export { gaUtils }
